import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { GraduationCap, Send, Award } from 'lucide-react';

const LEAD_TYPES = ['expired', 'fsbo', 'circle_prospect', 'buyer_lead', 'seller_lead', 'past_client', 'referral'];
const PERSONALITIES = ['friendly', 'skeptical', 'hostile', 'busy', 'curious'];
const DIFFICULTIES = ['easy', 'medium', 'hard'];
const OBJECTIONS = ['Already have an agent', 'Not interested', 'Commission too high', 'Selling it ourselves', 'Call me later'];

function tryParseJSON(text) {
  if (!text) return null;
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try { return JSON.parse(match[0]); } catch { return null; }
}

export default function CallPractice() {
  const [config, setConfig] = useState({
    lead_type: 'expired',
    difficulty: 'medium',
    personality: 'skeptical',
    objections: ['Already have an agent'],
    goal: 'Book a listing consultation',
  });
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [grade, setGrade] = useState(null);
  const [grading, setGrading] = useState(false);
  const bottom = useRef();

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, grade]);

  const start = async () => {
    setBusy(true); setGrade(null); setMessages([]);
    try {
      const { data } = await http.post('/call/practice/start', config);
      setSessionId(data.session_id);
      setMessages([{ role: 'lead', text: data.opening }]);
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !sessionId) return;
    const my = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'agent', text: my }]);
    setBusy(true);
    try {
      const { data } = await http.post('/call/practice/turn', { session_id: sessionId, agent_message: my });
      setMessages(m => [...m, { role: 'lead', text: data.reply }]);
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const gradeCall = async () => {
    if (!sessionId) return;
    setGrading(true);
    try {
      const { data } = await http.post('/call/practice/grade', { session_id: sessionId });
      const parsed = tryParseJSON(data.raw) || { raw: data.raw };
      setGrade(parsed);
    } catch (e) { toast.error(formatError(e)); }
    setGrading(false);
  };

  const toggleObj = (o) => {
    setConfig(c => ({ ...c, objections: c.objections.includes(o) ? c.objections.filter(x => x !== o) : [...c.objections, o] }));
  };

  return (
    <div className="space-y-6">
      <header className="hq-fade-in">
        <div className="flex items-center gap-3">
          <Link to="/call" className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>← Call Command</Link>
        </div>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="practice-title">Practice Mode</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Role-play with an AI lead. No real people are contacted. Get a fair, kind score that rewards
          meaningful conversation, respect, and compliance — not pressure.
        </p>
      </header>

      {!sessionId && (
        <div className="hq-card p-6 space-y-5 hq-fade-in">
          <div className="hq-plum-eyebrow">Scenario</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="hq-label">Lead Type</label>
              <select className="hq-input" value={config.lead_type} onChange={e => setConfig({...config, lead_type: e.target.value})} data-testid="config-lead-type">
                {LEAD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="hq-label">Difficulty</label>
              <select className="hq-input" value={config.difficulty} onChange={e => setConfig({...config, difficulty: e.target.value})} data-testid="config-difficulty">
                {DIFFICULTIES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="hq-label">Personality</label>
              <select className="hq-input" value={config.personality} onChange={e => setConfig({...config, personality: e.target.value})} data-testid="config-personality">
                {PERSONALITIES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="hq-label">Goal</label>
            <input className="hq-input" value={config.goal} onChange={e => setConfig({...config, goal: e.target.value})} />
          </div>
          <div>
            <label className="hq-label">Common Objections They'll Raise</label>
            <div className="flex flex-wrap gap-2">
              {OBJECTIONS.map(o => (
                <button
                  key={o}
                  type="button"
                  onClick={() => toggleObj(o)}
                  className="text-xs px-3 py-2 border rounded-sm"
                  data-testid={`obj-toggle-${o.slice(0,10).replace(/\s/g,'-')}`}
                  style={{
                    borderColor: config.objections.includes(o) ? 'var(--plum)' : 'var(--line)',
                    background: config.objections.includes(o) ? 'var(--plum-tint)' : '#fff',
                    color: config.objections.includes(o) ? 'var(--plum)' : 'var(--ink-soft)',
                  }}
                >{o}</button>
              ))}
            </div>
          </div>
          <button className="hq-btn-plum inline-flex items-center gap-2" onClick={start} disabled={busy} data-testid="start-practice-btn">
            <GraduationCap size={14} /> {busy ? 'Setting up…' : 'Start Practice'}
          </button>
        </div>
      )}

      {sessionId && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 hq-card p-6 flex flex-col" style={{ minHeight: '520px' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="hq-plum-eyebrow">Conversation</div>
              <button className="hq-btn-ghost" onClick={() => { setSessionId(null); setMessages([]); setGrade(null); }} data-testid="reset-practice-btn">Reset</button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 pr-2" data-testid="practice-transcript">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'agent' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className="max-w-[80%] p-3 text-sm"
                    style={{
                      background: m.role === 'agent' ? 'var(--sage)' : 'var(--plum-tint)',
                      color: m.role === 'agent' ? '#fff' : 'var(--ink)',
                      borderRadius: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    <div className="hq-eyebrow mb-1" style={{ color: m.role === 'agent' ? 'var(--gold)' : 'var(--plum)' }}>
                      {m.role === 'agent' ? 'You' : 'Lead'}
                    </div>
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && <div className="hq-shimmer h-10 rounded-sm w-2/3"></div>}
              <div ref={bottom}></div>
            </div>
            <form onSubmit={send} className="mt-4 flex gap-2">
              <input
                className="hq-input flex-1"
                placeholder="What do you say next?"
                value={input}
                onChange={e => setInput(e.target.value)}
                disabled={busy || grading}
                data-testid="practice-input"
              />
              <button className="hq-btn-plum" disabled={busy || grading || !input.trim()} data-testid="practice-send-btn">
                <Send size={13} />
              </button>
            </form>
          </div>

          <div className="hq-card p-6 space-y-4">
            <div className="hq-plum-eyebrow">Ready to end?</div>
            <p className="text-xs" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
              End the practice call and get a fair grade with 2 strengths, 2 things to improve, and a
              suggested improved response.
            </p>
            <button
              className="hq-btn-plum w-full inline-flex items-center justify-center gap-2"
              onClick={gradeCall}
              disabled={grading || messages.length < 2}
              data-testid="grade-btn"
            >
              <Award size={14} /> {grading ? 'Coaching…' : 'End & Grade'}
            </button>

            {grade && (
              <div className="space-y-4 pt-4" style={{ borderTop: '1px solid var(--line)' }} data-testid="grade-panel">
                {grade.scores && (
                  <div className="space-y-1.5">
                    <div className="hq-eyebrow mb-2">Scores</div>
                    {Object.entries(grade.scores).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <div className="w-32 uppercase tracking-widest" style={{ color: 'var(--muted)' }}>{k.replace(/_/g, ' ')}</div>
                        <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--line)' }}>
                          <div className="h-full rounded-full" style={{ width: `${(v/10)*100}%`, background: 'var(--plum)' }} />
                        </div>
                        <div style={{ color: 'var(--ink)' }}>{v}/10</div>
                      </div>
                    ))}
                  </div>
                )}
                {grade.strengths?.length > 0 && (
                  <div>
                    <div className="hq-eyebrow mb-1" style={{ color: 'var(--success)' }}>Strengths</div>
                    <ul className="text-sm space-y-1" style={{ color: 'var(--ink-soft)' }}>
                      {grade.strengths.map((s, i) => <li key={i}>· {s}</li>)}
                    </ul>
                  </div>
                )}
                {grade.improvements?.length > 0 && (
                  <div>
                    <div className="hq-eyebrow mb-1" style={{ color: 'var(--gold-soft)' }}>Improve</div>
                    <ul className="text-sm space-y-1" style={{ color: 'var(--ink-soft)' }}>
                      {grade.improvements.map((s, i) => <li key={i}>· {s}</li>)}
                    </ul>
                  </div>
                )}
                {grade.improved_example && (
                  <div>
                    <div className="hq-eyebrow mb-1">Try this instead</div>
                    <div className="text-sm font-serif italic p-3" style={{ background: 'var(--parchment)', color: 'var(--ink)', borderRadius: 2, lineHeight: 1.6 }}>
                      "{grade.improved_example}"
                    </div>
                  </div>
                )}
                {grade.raw && !grade.scores && (
                  <pre className="whitespace-pre-wrap text-xs" style={{ color: 'var(--ink-soft)' }}>{grade.raw}</pre>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
